import { type PlayerRatingAllResponse } from '@on3/api';
import {
  ServiceLogo,
  type ServiceType,
} from '@on3/ui-lib/components/ServiceLogo/ServiceLogo';
import clsx from 'clsx';
import { StarRating } from 'components/UI/StarRating/StarRating';
import { Text } from 'components/UI/Typography/Text';
import { useRouter } from 'next/router';

import { type IndustryComparisonRatingType, sortTypeDict } from './helpers';
import { IndustryComparisonConditionalLink } from './IndustryComparisonConditionalLink/IndustryComparisonConditionalLink';
import styles from './IndustryComparisonFourServicesItem.module.scss';

interface IIndustryComparisonRatingProps extends PlayerRatingAllResponse {
  slug?: string | null;
  selected?: string | null;
  /**Show jersey or overall rank column */
  showJerseyOrRank?: boolean;
}
export const IndustryComparisonServiceItem = ({
  rating,
  stars,
  type,
  slug,
  classYear,
  sport,
  selected,
  link,
  overallRank,
  stateRank,
  positionRank,
  showJerseyOrRank,
  rankingType,
}: IIndustryComparisonRatingProps) => {
  const profileLink = `/db/${slug}/recruiting/`;
  const router = useRouter();
  const { transferType } = router.query;
  const isTpRoute =
    transferType === 'wire' ||
    transferType === 'top' ||
    transferType === 'industry';
  const href = type?.toLowerCase() === 'on3' ? profileLink : link || '';

  const sortByIndustry = (selected?.toLowerCase() ??
    'consensus') as IndustryComparisonRatingType;

  const isActive =
    type?.toLowerCase() ===
    sortTypeDict[sortByIndustry as IndustryComparisonRatingType];

  return (
    <div
      className={clsx(styles.serviceItemWrapper, {
        [styles.active]: isActive,
      })}
    >
      <IndustryComparisonConditionalLink
        className={clsx(styles.serviceItemContainer, {
          [styles.noJerseyOrRank]: !showJerseyOrRank,
        })}
        data-name="service-ranking"
        href={href}
        isActive={isActive}
        type={type}
      >
        <div className={styles.serviceLogo}>
          <div className={styles.mobileLogoContainer}>
            <ServiceLogo type={type as ServiceType} />
          </div>
          <StarRating
            className={styles.serviceItemStarRating}
            grayRating
            hasBorder={false}
            isBoldRating={false}
            rankingType={rankingType}
            rating={rating}
            small
            sport={sport}
            stars={stars}
            type={type}
            year={`${classYear}`}
          />
        </div>
        <div className={clsx(styles.serviceItemRankingHeaderContainer)}>
          <Text
            aria-label={`${type} National Rank`}
            className={styles.rankingHeader}
            component="span"
            variant="subtitle1"
          >
            Natl
          </Text>
          <Text
            aria-label={`${type} Position Rank`}
            className={styles.rankingHeader}
            component="span"
            variant="subtitle1"
          >
            Pos
          </Text>
          {!isTpRoute && (
            <Text
              aria-label={`${type} State Rank`}
              className={styles.rankingHeader}
              component="span"
              variant="subtitle1"
            >
              St
            </Text>
          )}
        </div>
        <div className={styles.serviceItemRankingContainer}>
          <Text className={styles.rank} variant="subtitle1">
            {overallRank ?? '-'}
          </Text>
          <Text className={styles.rank} variant="subtitle1">
            {positionRank ?? '-'}
          </Text>
          {!isTpRoute && (
            <Text className={styles.rank} variant="subtitle1">
              {stateRank ?? '-'}
            </Text>
          )}
        </div>
      </IndustryComparisonConditionalLink>
    </div>
  );
};
